import React from 'react';
import { useSelector } from 'react-redux';

import { VideoStatus } from 'js/src/client';
import { SimpleButton, SPINNER_ICON } from 'js/src/components/common/ui/Button';
import { canSeeVideoControls } from 'js/src/data/account';
import {
    getSharePageVideoData,
    getSharePageVideoName,
    getSharePageVideoStatus,
} from 'js/src/data/share-video';
import { getTimeStringFromSeconds } from 'js/src/libs/video-duration-tools';
import { useLuminaryOperations } from 'js/src/luminary/luminary-hooks';
import { downloadLuminaryFrameAsImgOperation } from 'js/src/luminary/luminary-operations';

type OwnProps = {
    className?: string;
    timeMillis: number;
};

const SaveVideoFrameAsImageButton = React.memo<OwnProps>(function SaveVideoFrameAsImageButton({
    className,
    timeMillis,
}) {
    const { downloadLuminaryFrameAsImg } = useLuminaryOperations({
        downloadLuminaryFrameAsImgOperation,
    });

    const videoData = useSelector(getSharePageVideoData);
    const videoName = useSelector(getSharePageVideoName);
    const videoStatus = useSelector(getSharePageVideoStatus);
    const canDownload = useSelector(canSeeVideoControls);

    const [isLoading, setIsLoading] = React.useState(false);

    const onClick = React.useCallback(async () => {
        setIsLoading(true);
        await downloadLuminaryFrameAsImg(timeMillis, videoName, videoData.toJS());
        analytics.track('Video frame saved', {
            timeMillis,
        });
        setIsLoading(false);
    }, [downloadLuminaryFrameAsImg, videoData, videoName, timeMillis]);

    if (videoStatus !== VideoStatus.PUBLISHED || !canDownload) {
        return null;
    }

    return (
        <SimpleButton
            text={`Save current frame (${getTimeStringFromSeconds(timeMillis / 1000)})`}
            iconLeft={isLoading ? SPINNER_ICON : 'download'}
            className={className}
            disabled={isLoading}
            onClick={onClick}
        />
    );
});

export default SaveVideoFrameAsImageButton;
